import { useState, useEffect, useRef } from "react";
import "./FormSidebar.css";
import { useDispatch, useSelector } from "react-redux";
import { selectEditingForm, selectEditingQuestionId, selectUIStates, selectUser, setEditingForm, setEditingQuestionId, setUIStates } from "../../redux/features/appData";
import { FormQuestion } from "../form-question/FormQuestion";
import { CreateQuestionForFormPayload, FormChanges, FormExtended, iconPath, Question, QuestionType, UpdateFormChangesPayload, UpdateFormQuestionOrderPayload, UpdateQuestionConditionsPayload } from "effno-core";
import { isEmptyJSON } from "../../utils/utils";
import { addNewQuestionToOrder, getQuestionOrder } from "../shared/uiHelpers";
import { DroppableSpacer } from "./DroppableSpacer";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { apiFormHasEdits, apiUpdateFormChanges, apiUpdateFormQuestionOrder } from "../../services/FormService";
import { ICONS } from "../shared/media";
import { CirclePlus } from "lucide-react";
import { END_SETTINGS, QUESTION_TITLES } from "../config/questionSettings";
import { apiCreateQuestionForForm, apiDeleteQuestion, apiUpdateQuestionConditions } from "../../services/QuestionService";
import { FormQuestionDeleteModal } from "../form-question/FormQuestionDeleteModal";
import { Condition } from "../workflow/questionWorkflowConditions";

type FormSidebarProps = {
}

export const FormSidebar = ({} : FormSidebarProps) => {

    const dispatch = useDispatch();

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);
    const editingForm = useSelector(selectEditingForm);
    const editingQuestionId = useSelector(selectEditingQuestionId);

    const editingFormRef = useRef<FormExtended | undefined>(undefined);

    const [deleteConfirmIsOpen, setDeleteConfirmIsOpen] = useState(false);
    const [deleteQuestionId, setDeleteQuestionId] = useState("");

    useEffect(() => {
        editingFormRef.current = editingForm;
    }, [editingForm]);

    const handleAddQuestion = () => {
        dispatch(setUIStates({...uiStates, questionPickerOpen: true}));
    }

    const handleAddEndPage = async () => {
        
        const type = QuestionType.END
            
        const newQuestion : CreateQuestionForFormPayload = {
            formId: editingForm?.id || "",
            type,
            title: QUESTION_TITLES[type],
            description: type === QuestionType.END ? "Description (optional)" : "",
            conditions: "{}",
            settings: "{}",
            required: false
        }

        
        newQuestion.settings = JSON.stringify(END_SETTINGS);                
               
        const question = await apiCreateQuestionForForm(newQuestion);
        if (!question) {
            toast("Failed to create question", toastFail);
            return;
        }

        await apiFormHasEdits(editingForm?.id || "");
        
        if (editingForm) {
            const allQuestions : Question[] = editingForm.questions ? [...editingForm.questions] : [];
            allQuestions.push(question);

            const newOrder = addNewQuestionToOrder(editingForm.questions, question, editingForm?.questionOrder);

            dispatch(setEditingForm({
                ...editingForm, 
                hasEdits: true,
                questions: allQuestions,
                questionOrder: newOrder
            }));

            const payload: UpdateFormQuestionOrderPayload = {
                formId: editingForm.id,
                questionOrder: newOrder
            }

            const updatedForm = await apiUpdateFormQuestionOrder(payload);

            if (!updatedForm) {
                toast("Failed to update form question order", toastFail);
                return;
            }

            dispatch(setEditingQuestionId(question.id));
           
        }
          
    }
    
    const handleMoveQuestion = async(questionId: string, position: number) => {
        let finalPosition = position;
        const theForm = editingFormRef.current;

        if (!theForm?.questionOrder) {
            return;
        }

        const theOrder = JSON.parse(theForm.questionOrder);

        const index = theOrder.findIndex((id: string) => id === questionId);
    
        if (index === -1) {
          toast("Invalid question", toastFail);
          return;
        }

        const { nonEndQuestions, endQuestions } = getQuestionOrder(theForm?.questions, theForm?.questionOrder);
        const firstEndIndex = nonEndQuestions.length > 0 ? nonEndQuestions.length : 0;
        const question = theForm?.questions?.find((q: Question) => q.id === questionId);

        if (question?.type === QuestionType.END) {
            finalPosition = firstEndIndex + position;
        } 
        
        // console.log("Question type: ", question?.type);
        // console.log("Move question: " + questionId);
        // console.log("From position: " + index + " to position: " + finalPosition);
        // console.log("theForm order: ", theForm?.questionOrder);

        // effectively no change
        if (index === finalPosition) {
            console.log("No change: "   + index + " to position: " + finalPosition);
            return;
        }

        // Create a copy of the array
        const newOrder = [...theOrder];

        // console.log("newOrder: ", newOrder);
        // console.log("index: ", index);
        // console.log("position: ", position);
        // console.log("final position: ", finalPosition);


        // Remove the element from the original position
        const [element] = newOrder.splice(index, 1);

        // console.log("newOrder 1: ", newOrder);

        if (question?.type !== QuestionType.END) {
            if (finalPosition >= theForm?.questions.length) {
                newOrder.push(element); 
            } else if (position >= nonEndQuestions.length) {
                newOrder.splice(finalPosition-1, 0, element);  
            } else {
                newOrder.splice(finalPosition, 0, element);
            }
        } else {
            if (finalPosition >= theForm?.questions.length) {
                newOrder.push(element); 
            } else {
                newOrder.splice(finalPosition, 0, element);  
            }
        }

        // Insert the element at the new position or at the end
        // if (finalPosition >= newOrder.length) {
        //     newOrder.push(element);
        // } else {
        //     newOrder.splice(position, 0, element);
        // }

        // console.log("newOrder updated: ", newOrder);

        const newForm = { ...theForm };
        newForm.questionOrder = JSON.stringify(newOrder);
    
        await apiFormHasEdits(newForm.id);
        newForm.hasEdits = true;
        
        dispatch(setEditingForm(newForm));
        updateFormQuestionOrder(newForm.questionOrder)
    }

    const handleDelete = (questionId: string) => {
        setDeleteQuestionId(questionId);
        setDeleteConfirmIsOpen(true);
    }

    const deleteQuestion = async (questionId: string) => { 
        if (!editingForm?.questions || editingForm.questions.length === 0) {
          return;
        }
    
        // before we delete, figure out what question to show
        // this does it in visual order in the sidebar and not workflow order
        let newQuestionId : string | undefined = undefined;
        const index = editingForm.questions.findIndex((q) => q.id === questionId);
    
        if (index === -1) {
          toast("Invalid question", toastFail);
          return;     
        }
    
        if (editingForm.questions.length > 1) {
          // is it the last one
          if (index === (editingForm.questions.length - 1)) {
            newQuestionId = editingForm.questions[index - 1].id;
          } else {
            newQuestionId = editingForm.questions[index + 1].id;
          }
        } else {
          newQuestionId = undefined;
        }
    
        dispatch(setEditingQuestionId(newQuestionId));
    
        // now delete it
        const result = await apiDeleteQuestion(questionId);
    
        if (!result) {
          toast("Error deleting question", toastFail);
          return;
        }
    
        // delete all the conditions that reference this question
        const newQuestions : Question[] = [];
    
        for (let i = 0; i < editingForm.questions.length; i++) {
          if (editingForm.questions[i].id !== questionId) {
            const newConditions = JSON.parse(editingForm.questions[i].conditions) as Condition[] | undefined;
            
            if (newConditions && newConditions.length > 0) {
              const index = newConditions.findIndex((c) => c.destinationQuestionId === questionId);
    
              // is there at least one condition that references this question
              if (index === -1) {
                newQuestions.push({...editingForm.questions[i]});
                continue;
              } else {
                const updatedConditions = newConditions.filter((c) => c.destinationQuestionId !== questionId);
                newQuestions.push({...editingForm.questions[i], conditions: JSON.stringify(updatedConditions)});
    
                const payload : UpdateQuestionConditionsPayload = {
                  questionId: editingForm.questions[i].id,
                  conditions: JSON.stringify(updatedConditions),
                }
    
                const newQ = await apiUpdateQuestionConditions(payload);
    
                if (!newQ) {
                  toast("Error updating question conditions for question: " + editingForm.questions[i].id, toastFail);
                  // keep going anyway
                }
    
                continue;
              }
            } else {
              newQuestions.push({...editingForm.questions[i]});
              continue
            }
          }
        }
    
        // update the order
        const theOrder = JSON.parse(editingForm.questionOrder);
        const orderIndex = theOrder.findIndex((id: string) => id === questionId);
        theOrder.splice(orderIndex, 1);
    
        const newOrder = JSON.stringify(theOrder);
        const payload: UpdateFormQuestionOrderPayload = {
          formId: editingForm.id,
          questionOrder: newOrder,
        };
    
        let updatedForm = await apiUpdateFormQuestionOrder(payload);
    
        if (!updatedForm) {
          toast("Error updating question order", toastFail);
          return;
        }
    
        // update the form changes - find the mapping and if it exists, add a DELETE version of it
        // it won't exist if the question is a new one, so we just don't add a DELETE
    
        let changedForm : FormExtended | null = editingForm;
    
        let formChanges : FormChanges[] = isEmptyJSON(JSON.parse(editingForm.formChanges)) ?
          [] : JSON.parse(editingForm.formChanges);
    
        const changeMap = formChanges.find((c) => c.type === "MAP" && c.originalQuestionId === questionId);
    
        if (changeMap) {
          formChanges.unshift({
            type: "DELETE",
            originalQuestionId: changeMap.originalQuestionId,
            newQuestionId: changeMap.newQuestionId,
          });
    
          const formChangesPayload : UpdateFormChangesPayload = {
            formId: editingForm.id,
            formChanges: JSON.stringify(formChanges),
          }
    
          changedForm = await apiUpdateFormChanges(formChangesPayload);
    
          if (!changedForm) {
            toast("Error updating form changes", toastFail);
            return;
          }
        }
    
        // flag changes
        await apiFormHasEdits(changedForm.id);
    
        // now update redux
        const newForm = {...changedForm, hasEdits: true, questionOrder: newOrder, questions: newQuestions};
     
        dispatch(setEditingForm(newForm));
        setDeleteQuestionId("");
    
        toast("Question deleted", toastSuccess);
    }


    const updateFormQuestionOrder = (questionOrder: string) => {
        (async () => {
          if (editingForm) {
    
            const payload: UpdateFormQuestionOrderPayload = {
              formId: editingForm.id,
              questionOrder,
            };
    
            const updatedForm = await apiUpdateFormQuestionOrder(payload);
    
            if (!updatedForm) {
              toast("Error updating question order", toastFail);
              return;
            }
          }
        })();
    }
    
    // build order for regular and end questions
    const { nonEndQuestions, endQuestions } = getQuestionOrder(editingForm?.questions, editingForm?.questionOrder);
    const hasQuestions = nonEndQuestions.length > 0 || endQuestions.length > 0;

    // const endQuestions = editingForm?.questions?.filter((question: Question) => question.type === QuestionType.END);
    // const nonEndQuestions = editingForm?.questions?.filter((question: Question) => question.type !== QuestionType.END);
    // // console.log("[FormSidebar] editingForm: ", editingForm);

    return (
        <>
            {/* {hasQuestions && 
                <div 
                    className="form-sidebar-add"
                    onClick={handleAddQuestion}
                >
                    <img src={iconPath(ICONS.ADD, storeUser)} /> Add Question
                </div>
            } */}

            {hasQuestions &&
                <div className="form-sidebar-section">
                    Questions
                    <div 
                        className="form-sidebar-add"
                        onClick={handleAddQuestion}
                    >
                        <CirclePlus 
                            size={16}
                            color="grey"
                        />
                    </div>
                </div>
            }

            <div className="form-sidebar-questions">
                {nonEndQuestions && nonEndQuestions.map((question: Question, index: number) => {
                    return (
                       
                            <DroppableSpacer 
                                key={"spacer-" + index}
                                position={index}
                                accept="question"
                            >
                                <FormQuestion 
                                    key={"form-question-" + question.id}
                                    question={question}
                                    questionNumber={index + 1}
                                    isSelected={question.id === editingQuestionId}
                                    onMove={handleMoveQuestion}
                                    onDelete={handleDelete}
                                />
                            </DroppableSpacer>
                    )
                    }
                )}
                {nonEndQuestions && nonEndQuestions.length > 0 && (
                     <DroppableSpacer 
                        key={"spacer-" + nonEndQuestions.length}
                        position={nonEndQuestions.length}
                        accept="question"
                    />
                )}
                <div className="form-sidebar-end-cap">
                    {}
                </div>
            </div>

            {hasQuestions &&
                <div className="form-sidebar-section">
                    Thank You Pages
                    <div 
                        className="form-sidebar-add"
                        onClick={handleAddEndPage}
                    >
                        <CirclePlus 
                            size={16}
                            color="grey"
                        />
                    </div>
                </div>
            }

            <div className="form-sidebar-end">
               
                {endQuestions && endQuestions.map((question: Question, index: number) => {
                    return (
                      
                            <DroppableSpacer 
                                key={"spacer-end-" + index}
                                position={index}
                                accept="end"
                            >
                                <FormQuestion
                                    key={"form-question-end" + question.id}
                                    question={question}
                                    isSelected={question.id === editingQuestionId}
                                    onMove={handleMoveQuestion}
                                    onDelete={handleDelete}
                                />
                            </DroppableSpacer>
                    )
                    }
                )}
                {endQuestions && endQuestions.length > 0 && (
                     <DroppableSpacer 
                        key={"spacer-end-" + endQuestions.length}
                        position={endQuestions.length}
                        accept="end"
                    />
                )}
                {/* <div className="form-sidebar-end-cap">
                    {}
                </div> */}
            </div>

            {deleteConfirmIsOpen && 
                <FormQuestionDeleteModal 
                    isOpen={deleteConfirmIsOpen}
                    onClose={() => setDeleteConfirmIsOpen(false)}
                    onSubmit={() => {
                        setDeleteConfirmIsOpen(false);
                        deleteQuestion(deleteQuestionId);
                }}
                />
            }

        </>
    )
}